import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Card, CardBody } from '@/components/ui/Card'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { Button } from '@/components/ui/Button'
import { MoneyText } from '@/components/ui/MoneyText'
import { useProfile } from '@/auth/SessionProvider'
import { canWriteDepartment } from '@/auth/profile'
import { listArrears } from '@/data/dues'
import { downloadCsv } from '@/lib/csv'
import { sumNgwee, toInputText } from '@/lib/money'
import { lusakaToday } from '@/lib/periods'
import { formatZambianPhone } from '@/lib/phone'

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** 'YYYY-MM-DD' (or 'YYYY-MM') -> 'July 2026' without any Date parsing. */
function monthYear(date: string): string {
  const [y, m] = date.split('-')
  const name = MONTHS[Number(m) - 1]
  return name ? `${name} ${y}` : date
}

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

export function ArrearsPage({ deptId }: { deptId: string }) {
  const profile = useProfile()
  const canWrite = canWriteDepartment(profile, deptId)
  const q = useQuery({ queryKey: ['arrears', deptId], queryFn: () => listArrears(deptId) })

  if (q.isPending) {
    return (
      <SkeletonRows rows={4} />
    )
  }
  if (q.isError) {
    return (
      <Card>
        <CardBody>
          <p role="alert" className="text-body text-bad">{errText(q.error)}</p>
          <Button variant="secondary" size="lg" className="mt-3" onClick={() => void q.refetch()}>
            Try again
          </Button>
        </CardBody>
      </Card>
    )
  }

  const rows = q.data
  const total = sumNgwee(rows.map((r) => r.arrearsNgwee))

  const download = () => {
    const csv: (string | number)[][] = [['Name', 'Phone', 'Months behind', 'Amount owed (Kwacha)', 'Oldest unpaid month']]
    for (const r of rows) {
      csv.push([r.fullName, formatZambianPhone(r.phone), r.periodsBehind, Number(toInputText(r.arrearsNgwee)), monthYear(r.oldestUnpaidPeriod)])
    }
    downloadCsv(`dues-arrears-${lusakaToday()}.csv`, csv)
  }

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-title font-semibold text-ink">Who is behind</h2>

      {rows.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState title="Everyone is up to date." description="Nobody owes any dues right now." />
          </CardBody>
        </Card>
      ) : (
        <>
          <Card>
            <CardBody className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-body text-muted">Total owed</p>
                <p className="text-title font-semibold text-ink"><MoneyText ngwee={total} /></p>
                <p className="mt-1 text-muted">
                  {rows.length === 1 ? '1 person is behind' : `${rows.length} people are behind`}
                </p>
              </div>
              <Button variant="secondary" size="lg" onClick={download}>Download for Excel</Button>
            </CardBody>
          </Card>

          <Card>
            <ul className="divide-y divide-line">
              {rows.map((r) => (
                <li key={r.memberId} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <Link
                      to="/d/$deptId/members/$memberId"
                      params={{ deptId, memberId: r.memberId }}
                      className="inline-flex min-h-[44px] items-center font-medium text-brand hover:underline"
                    >
                      {r.fullName}
                    </Link>
                    {r.phone && (
                      <div>
                        <a href={`tel:${r.phone}`} className="inline-flex min-h-[44px] items-center text-body text-neutral underline">
                          {formatZambianPhone(r.phone)}
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-4 sm:justify-end">
                    <div className="sm:text-right">
                      <p className="font-semibold text-ink"><MoneyText ngwee={r.arrearsNgwee} /></p>
                      <p className="text-body text-muted">
                        {r.periodsBehind === 1 ? '1 month behind' : `${r.periodsBehind} months behind`}
                      </p>
                      <p className="text-micro text-muted">oldest: {monthYear(r.oldestUnpaidPeriod)}</p>
                    </div>
                    {canWrite && (
                      <Link
                        to="/d/$deptId/pay"
                        params={{ deptId }}
                        search={{ member: r.memberId }}
                        className="inline-flex min-h-[44px] items-center rounded-control bg-brand-tint px-3 text-body font-medium text-brand hover:bg-brand-tint"
                      >
                        Record payment
                      </Link>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}
    </div>
  )
}
